import React, { useEffect, useRef, useState } from "react";

type SharedCatalogueAuthActionsProps = {
  isVisitorSession: boolean;
  visitorName?: string;
  guestName?: string;
  catalogueUnlocked: boolean;
  onMySelections: () => void;
  onOpenAuth: () => void;
  onSignOutVisitor: () => void;
  onLeaveCollection: () => void;
  /** A reseller's client has no studio account to open. */
  hideAccount?: boolean;
};

function ProfileMenu({
  name,
  title,
  children,
}: {
  name: string;
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      className={`hkp-shared-account${open ? " is-open" : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="hkp-shared-profile"
        title={title}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="hkp-shared-signed-in">{name}</span>
      </button>
      {open ? (
        <div
          className="hkp-shared-account-menu"
          role="menu"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** Invite OTP vs visitor account are two sessions. Header never says Sign in after the link is unlocked. */
const SharedCatalogueAuthActions: React.FC<SharedCatalogueAuthActionsProps> = ({
  isVisitorSession,
  visitorName,
  guestName,
  catalogueUnlocked,
  onMySelections,
  onOpenAuth,
  onSignOutVisitor,
  onLeaveCollection,
  hideAccount = false,
}) => {
  if (isVisitorSession) {
    return (
      <ProfileMenu
        name={visitorName?.trim() || "Signed in"}
        title="Signed in to your account"
      >
        <button type="button" className="hkp-shared-linkish" onClick={onMySelections}>
          My selections
        </button>
        <button type="button" className="hkp-shared-linkish" onClick={onSignOutVisitor}>
          Sign out
        </button>
      </ProfileMenu>
    );
  }

  if (catalogueUnlocked) {
    return (
      <ProfileMenu
        name={guestName?.trim() || "Verified"}
        title="Verified for this collection"
      >
        {hideAccount ? null : (
          <button
            type="button"
            className="hkp-shared-linkish"
            onClick={onOpenAuth}
            title="Optional — save pieces to an account"
          >
            Account
          </button>
        )}
        <button
          type="button"
          className="hkp-shared-linkish"
          onClick={onLeaveCollection}
          title="Leave this collection"
        >
          Sign out
        </button>
      </ProfileMenu>
    );
  }

  return null;
};

export default SharedCatalogueAuthActions;
